import { memo, useCallback, useMemo } from 'react';
import { cn } from '@divlab/divanui';
import { IconArrowRight } from '@divlab/divanui/icons';

import * as ModalWindows from '@Stores/ModalWindows';
import * as RelatedProductsQueries from '@Queries/RelatedProducts';
import useTranslation from '@Queries/useTranslation';
import Link from '@UI/Link';
import ProductCard from './elements/ProductCard';
import styles from './KitsTab.module.css';

import type { HTMLAttributes, MouseEvent } from 'react';
import type { Kits, WarrantyData } from '@Pages/PageProduct/typings';
import type { ProductData } from '@Types/Product';

export interface KitsTabProps extends HTMLAttributes<HTMLDivElement> {
  kits: Kits[];
  mainId: number;
  warranty?: WarrantyData;
  disabled: boolean;
}

const KitsTab = (props: KitsTabProps) => {
  const { className, kits, mainId, disabled, ...restProps } = props;
  const { t } = useTranslation();
  const isKits = kits?.length > 0;
  const analyticsTitle = isKits ? t('kits.kitTitle') : t('kits.defaultTitle');
  const selectedProducts = RelatedProductsQueries.useSortedRelatedProductsBy('quantity', mainId);
  const { mutateAsync: changeQuantity } = RelatedProductsQueries.useChangeQuantity(mainId);
  const { apply } = RelatedProductsQueries.useApply(mainId);
  const checked = selectedProducts?.length > 0;

  const title = useMemo(() => {
    return kits[0].groups.find((group) => group.listTitle?.length > 0)?.listTitle;
  }, [kits]);

  const handleClick = useCallback(() => {
    if (disabled) return;

    ModalWindows.open('RelatedProducts', {
      mainId,
      analyticsTitle,
      singleSelection: true,
    });
  }, [disabled, mainId, analyticsTitle]);

  const handleRemoveProduct = useCallback(
    async (e: MouseEvent, product: ProductData) => {
      await changeQuantity({ product, quantity: 0 });
      apply();
    },
    [apply, changeQuantity],
  );

  return (
    <div
      {...restProps}
      className={cn(
        styles.tab,
        { [styles.disabled]: disabled },
        { [styles.checked]: checked },
        className,
      )}
    >
      <div className={styles.container}>
        <div className={styles.head} onClick={handleClick}>
          <svg
            className={styles.icon}
            xmlns='http://www.w3.org/2000/svg'
            width='30'
            height='30'
            viewBox='0 0 30 30'
            fill='none'
          >
            <path
              d='M15.2951 14.6499C15.3054 14.6748 15.3251 14.6946 15.35 14.705L15.4373 14.7411C15.4622 14.7514 15.4819 14.7712 15.4923 14.7961C15.5026 14.821 15.5026 14.849 15.4923 14.8739L15.4561 14.9611C15.4458 14.986 15.4458 15.014 15.4562 15.0389L15.4922 15.1261C15.4973 15.1384 15.5 15.1516 15.5 15.165C15.5 15.1783 15.4974 15.1916 15.4922 15.2039C15.4871 15.2162 15.4796 15.2275 15.4702 15.2369C15.4608 15.2463 15.4496 15.2538 15.4372 15.2589L15.35 15.295C15.3251 15.3053 15.3053 15.3251 15.295 15.35L15.2588 15.4373C15.2485 15.4622 15.2287 15.4819 15.2038 15.4923C15.1789 15.5026 15.151 15.5026 15.1261 15.4923L15.0389 15.4561C15.014 15.4458 14.986 15.4459 14.9611 15.4562L14.8738 15.4923C14.849 15.5026 14.821 15.5026 14.7961 15.4923C14.7712 15.482 14.7515 15.4622 14.7411 15.4373L14.705 15.35C14.6947 15.3251 14.6749 15.3053 14.65 15.295L14.5628 15.2589C14.5379 15.2485 14.5181 15.2288 14.5078 15.2039C14.4975 15.179 14.4975 15.151 14.5078 15.1261L14.5439 15.0389C14.5542 15.014 14.5541 14.9861 14.5438 14.9612L14.5077 14.8738C14.5026 14.8615 14.5 14.8483 14.5 14.8349C14.5 14.8216 14.5026 14.8084 14.5077 14.796C14.5128 14.7837 14.5203 14.7725 14.5298 14.763C14.5392 14.7536 14.5504 14.7461 14.5628 14.741L14.65 14.7049C14.6748 14.6946 14.6946 14.6749 14.705 14.65L14.7411 14.5627C14.7514 14.5378 14.7712 14.5181 14.7961 14.5077C14.821 14.4974 14.849 14.4974 14.8739 14.5077L14.9611 14.5439C14.986 14.5541 15.014 14.5541 15.0389 14.5438L15.1261 14.5078C15.151 14.4975 15.179 14.4975 15.2039 14.5078C15.2288 14.5181 15.2486 14.5379 15.2589 14.5628L15.2951 14.6501L15.2951 14.6499Z'
              fill='currentColor'
            />
            <path
              d='M22.3765 6.24813C22.6339 6.87067 23.1279 7.3655 23.7501 7.62384L25.9315 8.52747C26.5541 8.78535 27.0487 9.27999 27.3066 9.90257C27.5645 10.5252 27.5645 11.2247 27.3066 11.8473L26.4036 14.0273C26.1457 14.6501 26.1453 15.3503 26.4045 15.9729L27.3058 18.1522C27.4337 18.4606 27.4995 18.7911 27.4996 19.1249C27.4996 19.4587 27.4339 19.7893 27.3062 20.0977C27.1784 20.4061 26.9912 20.6863 26.7551 20.9223C26.519 21.1583 26.2388 21.3455 25.9303 21.4731L23.7504 22.3761C23.1279 22.6335 22.6331 23.1276 22.3748 23.7497L21.4712 25.9313C21.2133 26.5539 20.7187 27.0485 20.0961 27.3064C19.4736 27.5643 18.7741 27.5643 18.1515 27.3064L15.9716 26.4034C15.349 26.1462 14.6498 26.1467 14.0276 26.4049L11.8461 27.3072C11.2239 27.5645 10.525 27.5643 9.90296 27.3066C9.28091 27.0489 8.78656 26.5549 8.52851 25.9329L7.62465 23.7507C7.36725 23.1282 6.87318 22.6334 6.25106 22.375L4.06958 21.4714C3.44729 21.2136 2.95282 20.7193 2.69485 20.0971C2.43689 19.4749 2.43654 18.7757 2.69388 18.1532L3.59683 15.9732C3.85407 15.3506 3.85354 14.6513 3.59537 14.0291L2.69372 11.846C2.56588 11.5376 2.50005 11.2071 2.5 10.8733C2.49995 10.5395 2.56567 10.2089 2.6934 9.90054C2.82114 9.59214 3.00839 9.31194 3.24446 9.07594C3.48053 8.83994 3.76078 8.65277 4.06921 8.52513L6.24912 7.62215C6.87109 7.36496 7.36561 6.8715 7.62413 6.25007L8.52773 4.06851C8.7856 3.44593 9.28022 2.95129 9.90278 2.69341C10.5253 2.43553 11.2248 2.43553 11.8474 2.69341L14.0273 3.59639C14.6499 3.85364 15.3491 3.85312 15.9713 3.59493L18.1537 2.69481C18.7762 2.43707 19.4755 2.43713 20.098 2.69496C20.7204 2.95279 21.215 3.44729 21.4729 4.06971L22.3767 6.25191L22.3765 6.24813Z'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M11.625 11.625H11.6363M18.375 18.375H18.3863M19.5 10.5L10.5 19.5M12.1875 11.625C12.1875 11.9357 11.9357 12.1875 11.625 12.1875C11.3143 12.1875 11.0625 11.9357 11.0625 11.625C11.0625 11.3143 11.3143 11.0625 11.625 11.0625C11.9357 11.0625 12.1875 11.3143 12.1875 11.625ZM18.9375 18.375C18.9375 18.6857 18.6857 18.9375 18.375 18.9375C18.0643 18.9375 17.8125 18.6857 17.8125 18.375C17.8125 18.0643 18.0643 17.8125 18.375 17.8125C18.6857 17.8125 18.9375 18.0643 18.9375 18.375Z'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <div>{title && <div className={styles.title}>{title}</div>}</div>
          <div className={styles.action}>
            <Link className={styles.actionText} view='primary' underlined>
              {checked ? t('kits.action-change') : t('kits.action')}
            </Link>

            <IconArrowRight className={styles.iconArrowRight} />
          </div>
        </div>
        {(selectedProducts || []).map((product) => (
          <ProductCard
            className={styles.product}
            product={product}
            key={product.id}
            disabled={disabled}
            onRemove={handleRemoveProduct}
          />
        ))}

        <div className={cn(styles.action, { [styles.mobile]: true })} onClick={handleClick}>
          <Link className={styles.actionText} view='primary' underlined>
            {selectedProducts?.length > 0 ? t('kits.action-change') : t('kits.action')}
          </Link>
        </div>
      </div>
      <div className={styles.condition}>{t('kits.condition')}</div>
    </div>
  );
};

export default memo(KitsTab);
